import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../api/client';

const OnboardingContext = createContext(null);

// Initial state
const initialState = {
  loading: true,
  error: null,
  completed: false,
  currentStep: 0,
  data: {
    // Step 1: Goals & Name
    primary_goal: null,
    full_name: '',
    // Step 2: Career Profile
    career_status: null,
    graduation_date: null,
    years_experience: null,
    target_roles: [],
    preferred_locations: [],
    remote_preference: null,
    // Step 3: Skills
    skills: [],
    companies_worked: '',
    resume_uploaded: false,
    resume_path: null,
    // Step 4: Preferences
    email_notifications: {
      job_alerts: true,
      interview_reminders: true,
      deadline_reminders: true,
      weekly_digest: true,
    },
    theme_preference: 'dark',
    feedback: '',
  },
  saveStatus: 'idle', // 'idle' | 'saving' | 'saved' | 'error'
  lastSaved: null,
};

// Action types
const ACTIONS = {
  LOAD_START: 'LOAD_START',
  LOAD_SUCCESS: 'LOAD_SUCCESS',
  LOAD_ERROR: 'LOAD_ERROR',
  UPDATE_STEP_DATA: 'UPDATE_STEP_DATA',
  SET_CURRENT_STEP: 'SET_CURRENT_STEP',
  SAVE_START: 'SAVE_START',
  SAVE_SUCCESS: 'SAVE_SUCCESS',
  SAVE_ERROR: 'SAVE_ERROR',
  COMPLETE: 'COMPLETE',
  RESET: 'RESET',
};

// Reducer
function onboardingReducer(state, action) {
  switch (action.type) {
    case ACTIONS.LOAD_START:
      return { ...state, loading: true, error: null };

    case ACTIONS.LOAD_SUCCESS:
      return {
        ...state,
        loading: false,
        completed: action.payload.completed,
        currentStep: action.payload.current_step || 0,
        data: {
          ...state.data,
          ...action.payload.data,
          primary_goal: action.payload.primary_goal || state.data.primary_goal,
          full_name: action.payload.full_name || state.data.full_name,
          career_status: action.payload.career_status || state.data.career_status,
          target_roles: action.payload.target_roles || state.data.target_roles,
          skills: action.payload.skills || state.data.skills,
        },
      };

    case ACTIONS.LOAD_ERROR:
      return { ...state, loading: false, error: action.payload };

    case ACTIONS.UPDATE_STEP_DATA:
      return {
        ...state,
        data: {
          ...state.data,
          ...action.payload,
        },
      };

    case ACTIONS.SET_CURRENT_STEP:
      return { ...state, currentStep: action.payload };

    case ACTIONS.SAVE_START:
      return { ...state, saveStatus: 'saving' };

    case ACTIONS.SAVE_SUCCESS:
      return {
        ...state,
        saveStatus: 'saved',
        lastSaved: new Date(),
        currentStep: Math.max(state.currentStep, action.payload.current_step || state.currentStep),
      };

    case ACTIONS.SAVE_ERROR:
      return { ...state, saveStatus: 'error', error: action.payload };

    case ACTIONS.COMPLETE:
      return { ...state, completed: true };

    case ACTIONS.RESET:
      return initialState;

    default:
      return state;
  }
}

// Provider component
export function OnboardingProvider({ children }) {
  const [state, dispatch] = useReducer(onboardingReducer, initialState);
  const { user, getToken } = useAuth();

  // Load onboarding status on mount
  useEffect(() => {
    if (user) {
      loadOnboardingStatus();
    }
  }, [user]);

  const loadOnboardingStatus = async () => {
    dispatch({ type: ACTIONS.LOAD_START });
    try {
      const token = getToken();
      const data = await api.get('/api/onboarding/status', {
        headers: { Authorization: `Bearer ${token}` },
      });
      dispatch({ type: ACTIONS.LOAD_SUCCESS, payload: data });
    } catch (error) {
      console.error('Failed to load onboarding status:', error);
      dispatch({ type: ACTIONS.LOAD_ERROR, payload: error.message });
    }
  };

  // Update step data locally
  const updateStepData = useCallback((data) => {
    dispatch({ type: ACTIONS.UPDATE_STEP_DATA, payload: data });
  }, []);

  // Save step to server
  const saveStep = useCallback(async (stepNumber, stepData) => {
    dispatch({ type: ACTIONS.SAVE_START });
    try {
      const token = getToken();
      const data = await api.patch(`/api/onboarding/step/${stepNumber}`, stepData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      dispatch({ type: ACTIONS.SAVE_SUCCESS, payload: data });
      return { success: true };
    } catch (error) {
      console.error(`Failed to save step ${stepNumber}:`, error);
      dispatch({ type: ACTIONS.SAVE_ERROR, payload: error.message });
      return { success: false, error: error.message };
    }
  }, [getToken]);

  // Navigate to next step
  const nextStep = useCallback(() => {
    const newStep = Math.min(state.currentStep + 1, 4);
    dispatch({ type: ACTIONS.SET_CURRENT_STEP, payload: newStep });
    return newStep;
  }, [state.currentStep]);

  // Navigate to previous step
  const prevStep = useCallback(() => {
    const newStep = Math.max(state.currentStep - 1, 1);
    dispatch({ type: ACTIONS.SET_CURRENT_STEP, payload: newStep });
    return newStep;
  }, [state.currentStep]);

  // Go to specific step
  const goToStep = useCallback((step) => {
    if (step >= 1 && step <= 4) {
      dispatch({ type: ACTIONS.SET_CURRENT_STEP, payload: step });
    }
  }, []);

  // Complete onboarding
  const completeOnboarding = useCallback(async () => {
    try {
      const token = getToken();
      await api.post('/api/onboarding/complete', {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      dispatch({ type: ACTIONS.COMPLETE });
      return { success: true };
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
      return { success: false, error: error.message };
    }
  }, [getToken]);

  // Skip remaining steps
  const skipToFinish = useCallback(async () => {
    try {
      const token = getToken();
      await api.post('/api/onboarding/skip', {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      dispatch({ type: ACTIONS.COMPLETE });
      return { success: true };
    } catch (error) {
      console.error('Failed to skip onboarding:', error);
      return { success: false, error: error.message };
    }
  }, [getToken]);

  // Poll for resume parsing status
  const pollParsingStatus = useCallback(async (onProgress) => {
    const token = getToken();
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    const maxAttempts = 120; // 2 minutes max (1 second intervals)
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const response = await fetch(`${API_URL}/api/onboarding/resume-parse-status`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to check parsing status');
        }

        const status = await response.json();

        // Call progress callback if provided
        if (onProgress) {
          onProgress(status);
        }

        if (status.status === 'completed') {
          return {
            success: true,
            parsed: status.parsed,
            parsedResume: status.parsed_resume,
            message: status.message,
            generalizedResumeId: status.generalized_resume_id,
          };
        }

        if (status.status === 'failed') {
          return {
            success: false,
            parsed: false,
            message: status.message,
            error: status.error,
          };
        }

        // Still processing, wait and try again
        await new Promise(resolve => setTimeout(resolve, 1000));
        attempts++;

      } catch (error) {
        console.error('Error polling parsing status:', error);
        attempts++;
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    // Timeout
    return {
      success: false,
      parsed: false,
      message: 'Resume parsing timed out. Please try again.',
    };
  }, [getToken]);

  // Upload resume
  const uploadResume = useCallback(async (file, onProgress) => {
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', file);

      // Use raw axios client for FormData upload to avoid Content-Type issues
      // axios will auto-detect FormData and set correct multipart/form-data with boundary
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_URL}/api/onboarding/resume-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          // Don't set Content-Type - browser will set it with boundary for FormData
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Upload failed with status ${response.status}`);
      }

      const data = await response.json();

      dispatch({
        type: ACTIONS.UPDATE_STEP_DATA,
        payload: {
          resume_uploaded: true,
          resume_path: data.path,
        },
      });

      // If status is "processing", poll for results
      if (data.status === 'processing') {
        // Notify that upload is complete, parsing started
        if (onProgress) {
          onProgress({ status: 'parsing', progress: 5, message: 'Upload complete. Parsing resume...' });
        }

        // Poll for parsing status
        const parsingResult = await pollParsingStatus(onProgress);

        if (parsingResult.success && parsingResult.parsedResume) {
          dispatch({
            type: ACTIONS.UPDATE_STEP_DATA,
            payload: {
              resume_uploaded: true,
              resume_path: data.path,
              parsed_resume: parsingResult.parsedResume,
            },
          });
        }

        return {
          success: parsingResult.success,
          path: data.path,
          parsed: parsingResult.parsed || false,
          parsedResume: parsingResult.parsedResume || null,
          message: parsingResult.message,
          generalizedResumeId: parsingResult.generalizedResumeId,
        };
      }

      // Legacy sync response (shouldn't happen with new backend)
      return {
        success: true,
        path: data.path,
        parsed: data.parsed || false,
        parsedResume: data.parsed_resume || null,
        message: data.message,
      };
    } catch (error) {
      console.error('Failed to upload resume:', error);
      return { success: false, error: error.message };
    }
  }, [getToken, pollParsingStatus]);

  // Confirm parsed resume as master resume
  const confirmParsedResume = useCallback(async () => {
    try {
      const token = getToken();
      const data = await api.post('/api/onboarding/confirm-parsed-resume', {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return {
        success: data.success,
        message: data.message,
        masterPath: data.master_path,
        hasExisting: data.has_existing || false,
      };
    } catch (error) {
      console.error('Failed to confirm parsed resume:', error);
      return { success: false, error: error.message };
    }
  }, [getToken]);

  // Get job recommendations
  const getJobRecommendations = useCallback(async () => {
    try {
      const token = getToken();
      const data = await api.get('/api/onboarding/job-recommendations', {
        headers: { Authorization: `Bearer ${token}` },
      });
      return { success: true, jobs: data.jobs };
    } catch (error) {
      console.error('Failed to get job recommendations:', error);
      return { success: false, jobs: [] };
    }
  }, [getToken]);

  // Check if step is complete
  const isStepComplete = useCallback((step) => {
    return state.currentStep >= step;
  }, [state.currentStep]);

  // Check if can proceed to next step
  const canProceed = useCallback((step) => {
    const { data } = state;
    switch (step) {
      case 1:
        return !!data.primary_goal && !!data.full_name?.trim();
      case 2:
        // Only career status is required for step 2
        return !!data.career_status;
      case 3:
      case 4:
        return true; // Optional steps
      default:
        return false;
    }
  }, [state.data]);

  const value = {
    // State
    loading: state.loading,
    error: state.error,
    completed: state.completed,
    currentStep: state.currentStep,
    data: state.data,
    saveStatus: state.saveStatus,
    lastSaved: state.lastSaved,

    // Actions
    loadOnboardingStatus,
    updateStepData,
    saveStep,
    nextStep,
    prevStep,
    goToStep,
    completeOnboarding,
    skipToFinish,
    uploadResume,
    confirmParsedResume,
    getJobRecommendations,
    isStepComplete,
    canProceed,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

// Hook to use onboarding context
export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
}

export default OnboardingContext;
